import React, { useState } from 'react';
import { services } from '../data/services';
import ServiceCard from '../Components/ServiceCard';
import BACKGROUND from '../assets/microneedling.jpeg';

const Service: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<'all' | 'dauerhaufte haarentfernung' | 'skincare'>('all');

  const categories = [
    { id: 'dauerhaufte haarentfernung', label: 'Dauerhafte Haarentfernung' },
    { id: 'skincare', label: 'Skincare' },
    { id: 'lash & browlifting', label: 'Lash & Browlifting'},
    { id: 'extras', label: 'Extras'}
  ];

  // Gefilterte Services
  const filteredServices =
    activeCategory === 'all'
      ? services
      : services.filter((service) => service.category === activeCategory);

  // Für Dauerhafte Haarentfernung: Gruppierung nach Geschlecht
  const frauenServices = filteredServices.filter((s) => s.gender === 'frauen');
  const maennerServices = filteredServices.filter((s) => s.gender === 'maenner');
  const otherServices = filteredServices.filter((s) => !s.gender);

  // Für Skincare: Einzelbehandlungen (ohne Pakete) und Pakete separat
  const skincareIndividualServices = filteredServices.filter((s) => s.category === 'skincare' && (!s.belongs_to || s.belongs_to !== 'skincare'));
  const skincarePackageServices = filteredServices.filter((s) => s.category === 'skincare' && s.belongs_to === 'skincare');

  const shouldGroupByGender = activeCategory === 'dauerhaufte haarentfernung';
  const shouldGroupByPackage = activeCategory === 'skincare';

  return (
    <div className="bg-[#FAF7F2] min-h-screen flex flex-col">
      {/* Hero / Titel */}
      <section
        className="w-full h-[90vh] md:h-[100vh] bg-cover bg-center flex flex-col justify-center items-center text-center px-6 relative mb-8 md:mb-12"
        style={{
          backgroundImage: `url(${BACKGROUND})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
      >
        {/* Dezentes, dunkles Overlay für besseren Kontrast */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-none" />
  
        {/* Content oben drüber */}
        <div className="relative z-10 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-light text-[#F3EEE7] drop-shadow-md">
            Unsere Leistungen
          </h1>
          <p className="mt-4 text-[#E7DACB] text-lg md:text-xl leading-relaxed drop-shadow-sm">
            Entdecke unsere professionellen Behandlungen. Qualität, Eleganz und Wohlbefinden stehen bei uns im Mittelpunkt.
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <div className="flex justify-center mb-12 flex-wrap gap-4">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id as any)}
            className={`px-6 py-2 rounded-full font-medium transition
              ${activeCategory === cat.id
                ? 'bg-[#C7B9A5] text-white'
                : 'bg-white border border-[#C7B9A5] text-[#7A6E62] hover:bg-[#f3eee7]'
              }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Services Grid */}
      <section className="max-w-6xl mx-auto w-full px-4">
        {shouldGroupByGender ? (
          <>
            {/* Frauen Sektion */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-light text-[#7A6E62] mb-8">
                Frauen
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
                {frauenServices.map((service) => (
                  <ServiceCard key={`${service.id}-${service.name}`} service={service} />
                ))}
              </div>
            </div>

            {/* Männer Sektion */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-light text-[#7A6E62] mb-8">
                Männer
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
                {maennerServices.map((service) => (
                  <ServiceCard key={`${service.id}-${service.name}`} service={service} />
                ))}
              </div>
            </div>
          </>
        ) : shouldGroupByPackage ? (
          <>
            {/* Einzelbehandlungen Sektion */}
            {skincareIndividualServices.length > 0 && (
              <div className="mb-16">
                <h2 className="text-3xl md:text-4xl font-light text-[#7A6E62] mb-8">
                  Einzelbehandlungen
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
                  {skincareIndividualServices.map((service) => (
                    <ServiceCard key={`${service.id}-${service.name}`} service={service} />
                  ))}
                </div>
              </div>
            )}

            {/* Pakete Sektion */}
            {skincarePackageServices.length > 0 && (
              <div className="mb-16">
                <h2 className="text-3xl md:text-4xl font-light text-[#7A6E62] mb-8">
                  Pakete
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
                  {skincarePackageServices.map((service) => (
                    <ServiceCard key={`${service.id}-${service.name}`} service={service} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {filteredServices.map((service) => (
              <ServiceCard key={`${service.id}-${service.name}`} service={service} />
            ))}
          </div>
        )}
      </section>

      {/* Sticky Pfeil unten */}
      <div className="sticky bottom-4 flex justify-center">
        <a href="#next-section" className="animate-bounce">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-10 w-10 text-gray-800"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </a>
      </div>
    </div>
  );
};

export default Service;
